import noop from 'lodash/noop';
import { push } from 'react-router-redux';
import { call, cancel, put, race, select, take } from 'redux-saga/effects';

import * as AppActions from '../../../actions';
import * as AppConstants from '../../../constants';
import { getInDomain } from '../../../domains/externalUtils';
import { putAndWait } from '../../../sagas/TaskManager';
import * as AppSelectors from '../../../selectors';
import Logger from '../../../tools/Logger';
import * as AppUtils from '../../../utils';
import * as MediatorConstants from '../constants';

const log = Logger.child({
  component: 'mediator/useCases/initiatePremiumAccess/helpers',
});

// eslint-disable-next-line require-yield
export function* getCoachmarkDisplayPandoraType(action) {
  const { targetId, targetTrackId } = action.data;
  if (targetTrackId) {
    return AppConstants.SourceTypes.PandoraTypes.SONG;
  }

  const type = AppUtils.SourceType.getPandoraTypeFromPandoraId(targetId);
  switch (type) {
    case AppConstants.SourceTypes.PandoraTypes.ALBUM:
    case AppConstants.SourceTypes.PandoraTypes.PLAYLIST:
    case AppConstants.SourceTypes.PandoraTypes.ARTIST:
      return type;
    default:
      return AppConstants.SourceTypes.PandoraTypes.SONG;
  }
}

export function* enforceLoggedInUser(action) {
  const { context, target } = action.data;
  let wasPlayingWhenStarted = yield select(getInDomain('playback.utils.isAudioPlaying', true));
  let isLoggedIn = yield select(AppUtils.Listener.isLoggedIn);
  let isAnonymousListener = yield select(AppUtils.Listener.isAnonymousListener);
  if (!isLoggedIn || isAnonymousListener) {
    yield put(AppActions.Login.setAfterLoginRedirect(location.pathname));
    yield put(push(AppUtils.Route.getRegisterPath()));
    const sourceType =
      AppConstants.SourceTypes.SourceTypes[AppConstants.SourceTypes.PandoraTypeSources[target]];
    yield put(
      AppActions.Modal.openModalCoachmark({
        ...MediatorConstants.messages.signUpOrLogInForPremium,
        values: { sourceType: sourceType.toLowerCase() },
      }),
    );
    const stationId = yield select(getInDomain('playback.utils.getCurrentPlaylist'));
    while (!isLoggedIn || isAnonymousListener) {
      yield take(AppConstants.Listener.ListenerActions.LISTENER_AUTHENTICATED);
      isLoggedIn = yield select(AppUtils.Listener.isLoggedIn);
      isAnonymousListener = yield select(AppUtils.Listener.isAnonymousListener);
    }
    if (yield select(AppUtils.Listener.hasPremiumFeatures)) {
      yield cancel();
      return;
    } else if (context === AppConstants.Ads.Rewards.ContextTypes.ARTIST_ONLY) {
      if (!(yield select(getInDomain('playback.utils.isOwnStation'), stationId))) {
        yield cancel();
        return;
      }
    } else if (stationId) {
      wasPlayingWhenStarted = false;
      yield put(getInDomain('playback.actions.resetPlaybackStarted')());
    }
  }
  yield put(getInDomain('rewards.actions.saveWasPlayingWhenStarted', noop)(wasPlayingWhenStarted));
}

export function* isPAEligible() {
  const isLoggedIn = yield select(AppUtils.Listener.isLoggedIn);
  const isAnonymousListener = yield select(AppUtils.Listener.isAnonymousListener);
  return isLoggedIn && !isAnonymousListener;
}

export function* openCoachmark(action) {
  log.info('Open coachmark');
  const { targetId, targetTrackId } = action.data;
  const annotationId =
    targetTrackId || (yield select(getInDomain('catalog.utils.mapPseudoSourceToParent'), targetId));
  const annotation = yield select(getInDomain('catalog.utils.getAnnotations'), annotationId);
  // eslint-disable-next-line require-atomic-updates
  action.data.iconUrl = AppUtils.Art.getArt(annotation, 160, yield select());

  yield put(AppActions.UseCases.pauseTrack());
  yield put(
    getInDomain('rewards.actions.savePlayableSourceId')(
      targetId,
      getInDomain('rewards.constants.ReducerKeys').PREMIUM_ACCESS,
    ),
  );
  yield put(getInDomain('displayAds.actions.setHasDeferredCompanionAd')(true));
  yield put(getInDomain('rewards.actions.openPremiumAccessCoachmark')(action.data));
}

export const coachmarkClosedActions = {
  start: take(AppConstants.Ads.Rewards.ActionTypes.START_PREMIUM_ACCESS_AND_PLAY),
  dismissVideo: take(AppConstants.Ads.Video.Actions.FLEX_VIDEO_HAS_DISMISSED),
  dismissCoachmark: take(_action => {
    return (
      _action.type === AppConstants.UseCases.ActionTypes.CLOSE_REWARDS_COACHMARK &&
      _action.data &&
      _action.data.reason !==
        AppConstants.External.CoachmarkDismissReasons.ACCEPT_INVITATION_COMPLETE
    );
  }),
};

let _restartPlaybackOverrideCounter = 0;
export function* waitForCoachmarkClose() {
  const next = yield race(coachmarkClosedActions);
  log.info('Coachmark closed');
  if (next.dismissVideo || next.dismissCoachmark) {
    log.info('Coachmark was dismissed');
    let trackingUrls = [];
    _restartPlaybackOverrideCounter =
      (yield select(getInDomain('application.utils.getRoutedCount'))) +
      _restartPlaybackOverrideCounter +
      1;

    if (next.dismissCoachmark) {
      trackingUrls = yield select(getInDomain('rewards.utils.getUrlsForDismissEvent', []));
    }

    const isAudioPlaying = yield select(getInDomain('playback.utils.isAudioPlaying', true));
    const wasAudioPlayingWhenStarted = yield select(
      getInDomain('rewards.utils.getWasPlayingWhenStarted'),
    );
    if (!isAudioPlaying && (wasAudioPlayingWhenStarted || _restartPlaybackOverrideCounter < 2)) {
      const trackTime = yield select(getInDomain('playback.utils.getCurrentTrackTime'));
      yield put(
        AppActions.Playback.intentPlayTrack({
          seekToInSeconds: trackTime > 0 ? trackTime : null,
        }),
      );
    }

    for (const url of trackingUrls) {
      yield call(getInDomain('analytics.utils.pingTrackingUrl'), url);
    }

    yield put(getInDomain('rewards.actions.clearOpenCoachmark')());
    yield put(getInDomain('displayAds.actions.setHasDeferredCompanionAd')(false));
    yield cancel();
  }
}

export function* triggerReauth() {
  log.info('Trigger reauth');
  yield call(putAndWait, AppActions.Application.listenerReauth({}));
  yield put(getInDomain('displayAds.actions.setShouldShowDisplayAdsToT2')(true));
}

export function* triggerWelcome() {
  const premiumAccessAudioData = yield select(
    getInDomain('listener.utils.getPremiumAccessFTUXEducationAudioData'),
  );
  log.info({ premiumAccessAudioData }, 'playWelcomeAudio');
  yield call(
    putAndWait,
    AppActions.SamplePlayer.playSample(
      premiumAccessAudioData.url,
      premiumAccessAudioData.resumeTrackOnEnd,
    ),
  );
  yield call(
    putAndWait,
    getInDomain('listener.actions.incrementPremiumAccessEducationWelcomeMessageCount')(),
  );
  yield put(
    getInDomain('analytics.actions.logPlayPAWelcomeAudio', null)(premiumAccessAudioData.sourceType),
  );
  yield put(
    AppActions.MiniCoachmark.openMiniCoachmark(
      AppConstants.MiniCoachmark.MiniCoachmarkTypes.FIRST_TIME_PREMIUM_ACCESS,
    ),
  );
}

export function* fallbackToUpsell(action) {
  log.info('Showing upsell instead of Premium Access');
  const isTrialEligible = yield select(AppSelectors.Payments.isTrialEligible);
  yield put(AppActions.Modal.openPremiumUpsellModal(action.data.target, isTrialEligible));
}
